'use client';

import { Component, createContext, useContext } from 'react';

//* Helpers
import Variables from '@/styles/variables';
import Typography from '@/styles/typography';
import HelperClass from '@/styles/helperClass';
import RewriteStyle from '@/styles/RewriteStyle';

const UIContext = createContext(null);

class UIProvider extends Component {
	//! States
	state = {
		winWidth: 1920,
		winHeight: 1080,
		screenSizes: {},
		preloader: true,
		highComponent: null,
		popupIsOpen: false,
		popupComponent: null,
		popupProps: {},
		isBurgerMenuOpen: false,
		firstRenderOfAnimation: true,
		updateFooterState: false,
		isTouchScreen: typeof window !== 'undefined' && 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0,
		loadAnimation: false,
		pageLoading: false,
	};

	pageLoadingCount = 0;

	//! Methods
	methods = {
		addHighComponent: this.addHighComponent.bind(this),
		removeHighComponent: this.removeHighComponent.bind(this),
		toggleBurgerMenu: this.toggleBurgerMenu.bind(this),
		closeBurgerMenu: this.closeBurgerMenu.bind(this),
		disablePreloader: this.disablePreloader.bind(this),
		loadAnimationPage: this.loadAnimationPage.bind(this),
		startPageLoading: this.startPageLoading.bind(this),
		stopPageLoading: this.stopPageLoading.bind(this),
		resetPageLoading: this.resetPageLoading.bind(this),
	};

	//! Update Page Animation
	loadAnimationPage() {
		this.setState({ loadAnimation: true });
	}

	startPageLoading() {
		this.pageLoadingCount += 1;
		if (!this.state.pageLoading) {
			this.setState({ pageLoading: true });
		}
	}

	stopPageLoading() {
		this.pageLoadingCount = Math.max(0, this.pageLoadingCount - 1);
		if (this.pageLoadingCount === 0 && this.state.pageLoading) {
			this.setState({ pageLoading: false });
		}
	}

	resetPageLoading() {
		this.pageLoadingCount = 0;
		if (this.state.pageLoading) {
			this.setState({ pageLoading: false });
		}
	}

	//! Screen Resize
	screenResize = () => {
		this.setState({
			winWidth: window.innerWidth,
			winHeight: window.innerHeight,
		});
	};

	//! Toggle Burger Menu
	toggleBurgerMenu() {
		this.setState({ isBurgerMenuOpen: !this.state.isBurgerMenuOpen });
	}

	//! Close Burger Menu
	closeBurgerMenu() {
		this.state.isBurgerMenuOpen && this.setState({ isBurgerMenuOpen: false });
	}

	//! Disable Preloader
	disablePreloader() {
		this.setState({ preloader: false });
	}

	//! Add High Component
	addHighComponent(component = null) {
		this.setState({
			highComponent: component,
		});
	}

	updateHighComponentProps(props) {
		const Comp = this.state.component;
		Comp.props = props;

		this.setState({
			highComponent: Comp,
		});
	}

	//! Remove High Component
	removeHighComponent() {
		this.setState({
			highComponent: null,
		});
	}

	//! Component Did Mount
	componentDidMount() {
		this.setState({
			...this.state,
		});

		window.addEventListener('resize', this.screenResize);
		this.screenResize();
	}

	//! Component Will Unmount
	componentWillUnmount() {
		window.addEventListener('resize', this.screenResize);
		document.removeEventListener('keydown', this.escKeydown, false);
	}

	render() {
		return (
			<UIContext.Provider value={{ ...this.state, ...this.methods }}>
				<Variables />
				<HelperClass />
				<Typography
					isBurgerMenuOpen={this.state.isBurgerMenuOpen}
					popupIsOpen={this.state.popupIsOpen}
				/>
				<RewriteStyle />

				{this.props.children}
			</UIContext.Provider>
		);
	}
}

export default UIProvider;
export const UIConsumer = UIContext.Consumer;

export const useDemoUI = () => {
	const context = useContext(UIContext);
	if (!context) {
		throw new Error('useDemoUI must be used within UIProvider');
	}
	return context;
};
